/**
 * BrokerageLeadModal — create / edit an Indirect Sales lead.
 *
 * The Indirect Sales module had no lead create/edit modal (only a
 * read-only detail dialog), so this IS the IS lead modal. Its defining
 * extra over a plain lead form is the **Brokerage** dropdown, bound to the
 * brokerage registry — when the modal is opened from inside a brokerage
 * detail screen that brokerage is pre-selected.
 *
 * Leads are stored in the existing `broker_sales` collection, so a lead
 * created here shows up on the Indirect Sales dashboard unchanged.
 *
 * Spec-driven extensions:
 *  - Project Enquired (required): multi-select bound to the projects
 *    master via `/api/projects` so newly added projects appear
 *    automatically without code changes here.
 *  - Client Budget (optional): two AED inputs — leave one blank for a
 *    single-figure budget, fill both for a range. Display logic on
 *    the dashboard handles "AED 1.5M" vs "AED 1.5M–2M" automatically.
 *  - Notes / Main Overview (optional): a separate long-text field
 *    (`lead_overview`) for free-form client preferences / broker
 *    conversation. Distinct from `source_notes` which captures how
 *    the lead came in.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Check, ChevronDown, X, FileText, CalendarDays,
  Building, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { createLead, updateLead } from '../api/brokerages';
import LeadDocumentsPanel from './LeadDocumentsPanel';
import DuplicateCheckModal from '../../CustomerMaster/components/DuplicateCheckModal';
import { checkDuplicate } from '../../CustomerMaster/api/customerMaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status funnel mirrors HUB_LEAD_FUNNEL in brokerage_hub_routes.py. The
// six funnel steps plus the two side-terminals (lost, cancelled).
const LEAD_STATUSES = [
  'new', 'contacted', 'viewing', 'negotiation',
  'booking_initiated', 'won', 'lost', 'cancelled',
];
// Display labels for the funnel statuses. Internal values stay the
// same (no DB migration needed) — these labels are purely cosmetic so
// the UI matches the language used in the 05 Jun 2026 MoM (§7): "New
// / Prospecting / Viewing / Negotiation / Won / Lost".
const LEAD_STATUS_LABELS = {
  new: 'New Lead',
  contacted: 'Prospecting',
  viewing: 'Site Visit',
  negotiation: 'Negotiation',
  booking_initiated: 'Booking Initiated',
  won: 'Booked / Won',
  lost: 'Lost',
  cancelled: 'Cancelled',
};

const emptyForm = {
  client_name: '',
  client_mobile: '',
  client_email: '',
  // Customer profile additions (UAE + spec).
  customer_nationality: '',
  preferred_contact_method: '',
  project_name: '',                // legacy single field — kept for back-compat
  project_enquired_ids: [],        // new: source of truth for the multi-select
  project_enquired_names: [],      // new: denormalized names for list views
  unit_type: '',
  property_type: '',
  preferred_unit_number: '',
  preferred_location: '',
  status: 'new',
  payout_amount: '',
  commission_percent: '',
  // Commission tracking — distinct from `payment_status` (which is the
  // customer paying for the unit). These two answer broker-side
  // questions: do they qualify, and have we paid them yet?
  commission_eligibility: 'pending',
  commission_payment_status: 'pending',
  client_budget_min: '',           // new: AED (optional)
  client_budget_max: '',           // new: AED (optional, range only)
  assigned_to_name: '',
  team_branch: '',
  manager_name: '',
  source_notes: '',
  lead_overview: '',               // new: Notes / Main Overview
  broker_id: '',
  // Structured site-visit capture — paired with the `viewing` status
  // step. Backend auto-stamps `site_visit_date` when status first
  // moves to viewing; users can edit it here at any point.
  site_visit_date: '',
  site_visit_agent: '',
  site_visit_feedback: '',
  site_visit_outcome: '',
  // Follow-up cadence — backend auto-stamps last_follow_up_at on any
  // update, but next_follow_up_at is user-set.
  next_follow_up_at: '',
  meeting_details: '',
  // Booking sub-record — only meaningful past booking_initiated.
  booked_unit_number: '',
  booking_amount: '',
  booking_date: '',
  reservation_notes: '',
};

// Common UAE property classifications. Free-text is still accepted on
// the backend, but the dropdown nudges everyone to a consistent label
// so the dashboard can group cleanly.
const PROPERTY_TYPES = [
  'Apartment', 'Villa', 'Townhouse', 'Penthouse', 'Duplex',
  'Office', 'Retail', 'Land Plot', 'Hotel Apartment', 'Other',
];

const CONTACT_METHODS = ['Phone Call', 'WhatsApp', 'Email', 'SMS', 'In Person'];

const COMMISSION_ELIGIBILITY = [
  { value: 'eligible',     label: 'Eligible' },
  { value: 'pending',      label: 'Pending review' },
  { value: 'not_eligible', label: 'Not eligible' },
  { value: 'on_hold',      label: 'On hold' },
];

const COMMISSION_PAYMENT_STATUS = [
  { value: 'pending',    label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'paid',       label: 'Paid' },
  { value: 'withheld',   label: 'Withheld' },
];

export default function BrokerageLeadModal({
  open,
  onOpenChange,
  lead,
  brokerages = [],
  defaultBrokerageId,
  onSaved,
}) {
  const { token } = useAuth();
  const isEdit = Boolean(lead?.id);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Pull the live project list once the modal opens. New projects show
  // up automatically because we re-fetch on every `open` transition.
  // Failure is non-fatal — the multi-select degrades to "no projects
  // available, type in the legacy text field" so the form still
  // saves.
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/projects?token=${token}`);
        if (!alive) return;
        setProjects(res.data?.projects || []);
        setProjectsLoaded(true);
      } catch (_) {
        if (alive) setProjectsLoaded(true);  // surface a degraded state
      }
    })();
    return () => { alive = false; };
  }, [open, token]);

  useEffect(() => {
    if (!open) return;
    setForm(
      lead
        ? {
            client_name: lead.client_name || '',
            client_mobile: lead.client_mobile || lead.client_phone || '',
            client_email: lead.client_email || '',
            project_name: lead.project_name || '',
            // Hydrate the multi-select from whichever shape the
            // server has on this lead. Legacy rows only carry
            // `project_name` (a single string) — we project that into
            // the names array so the user sees their existing
            // selection on first edit, even before the row was ever
            // saved with the new multi-select shape.
            project_enquired_ids: Array.isArray(lead.project_enquired_ids)
              ? lead.project_enquired_ids
              : [],
            project_enquired_names: Array.isArray(lead.project_enquired_names) && lead.project_enquired_names.length
              ? lead.project_enquired_names
              : (lead.project_name ? [lead.project_name] : []),
            unit_type: lead.unit_type || '',
            status: (lead.status || 'new').toLowerCase(),
            payout_amount: lead.payout_amount ?? '',
            commission_percent: lead.commission_percent ?? '',
            client_budget_min: lead.client_budget_min ?? '',
            client_budget_max: lead.client_budget_max ?? '',
            assigned_to_name: lead.assigned_to_name || lead.assigned_agent_name || '',
            source_notes: lead.source_notes || '',
            lead_overview: lead.lead_overview || '',
            broker_id: lead.broker_id || defaultBrokerageId || '',
            // Site-visit fields. `site_visit_date` is stored as an ISO
            // string on the server — slice off the time bit so the
            // <input type="datetime-local"> control accepts it.
            site_visit_date: lead.site_visit_date
              ? new Date(lead.site_visit_date).toISOString().slice(0, 16)
              : '',
            site_visit_agent: lead.site_visit_agent || '',
            site_visit_feedback: lead.site_visit_feedback || '',
            site_visit_outcome: lead.site_visit_outcome || '',
            // Spec-extension hydration.
            customer_nationality: lead.customer_nationality || '',
            preferred_contact_method: lead.preferred_contact_method || '',
            property_type: lead.property_type || '',
            preferred_unit_number: lead.preferred_unit_number || '',
            preferred_location: lead.preferred_location || '',
            team_branch: lead.team_branch || '',
            manager_name: lead.manager_name || '',
            commission_eligibility: lead.commission_eligibility || 'pending',
            commission_payment_status: lead.commission_payment_status || 'pending',
            next_follow_up_at: lead.next_follow_up_at
              ? new Date(lead.next_follow_up_at).toISOString().slice(0, 16)
              : '',
            meeting_details: lead.meeting_details || '',
            booked_unit_number: lead.booked_unit_number || '',
            booking_amount: lead.booking_amount ?? '',
            booking_date: lead.booking_date
              ? new Date(lead.booking_date).toISOString().slice(0, 16)
              : '',
            reservation_notes: lead.reservation_notes || '',
          }
        : { ...emptyForm, broker_id: defaultBrokerageId || '' },
    );
  }, [open, lead, defaultBrokerageId]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Duplicate check state — only relevant on the create path. For
  // edits we skip the check entirely (you're editing the very record
  // a check would flag).
  const [dupOpen, setDupOpen] = useState(false);
  const [dupResult, setDupResult] = useState(null);
  // The payload we'd post once the user confirms (either by picking
  // "create anyway" or by no duplicates being found). Stashed so the
  // duplicate modal can forward it to the force-duplicate request.
  const [pendingPayload, setPendingPayload] = useState(null);

  const buildPayload = (extras = {}) => {
    const min = form.client_budget_min === '' ? null : Number(form.client_budget_min);
    const max = form.client_budget_max === '' ? null : Number(form.client_budget_max);
    return {
      ...form,
      client_name: form.client_name.trim(),
      broker_id: form.broker_id || null,
      payout_amount: form.payout_amount === '' ? null : Number(form.payout_amount),
      commission_percent:
        form.commission_percent === '' ? null : Number(form.commission_percent),
      client_budget_min: min,
      client_budget_max: max,
      client_budget_currency: 'AED',
      site_visit_date: form.site_visit_date
        ? new Date(form.site_visit_date).toISOString()
        : null,
      next_follow_up_at: form.next_follow_up_at
        ? new Date(form.next_follow_up_at).toISOString()
        : null,
      booking_date: form.booking_date
        ? new Date(form.booking_date).toISOString()
        : null,
      booking_amount: form.booking_amount === '' ? null : Number(form.booking_amount),
      ...extras,
    };
  };

  // Actually POST the lead. Pulled out of handleSave so both the
  // direct save path (no duplicates) and the Option-B "Create New
  // Opportunity" path can share it. customerId is forwarded so the
  // backend stamps the lead with the same master id as the existing
  // opportunities.
  const persistLead = async (customerId = null) => {
    const payload = buildPayload(customerId ? { customer_id: customerId } : {});
    const saved = isEdit
      ? await updateLead(token, lead.id, payload)
      : await createLead(token, payload);
    toast.success(isEdit ? 'Lead updated' : 'Lead added');
    onSaved?.(saved);
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!form.client_name.trim()) {
      toast.error('Client name is required');
      return;
    }
    // Project Enquired is required per the spec. We accept EITHER the
    // multi-select having a value OR the legacy text input being
    // populated (to keep edits-on-old-rows non-blocking when the
    // projects list happens to be empty / errored).
    const hasProjectSelection = (
      (form.project_enquired_names && form.project_enquired_names.length > 0)
      || !!form.project_name.trim()
    );
    if (!hasProjectSelection) {
      toast.error('Project enquired is required — pick at least one project');
      return;
    }
    // Budget sanity: if both are filled, min must be ≤ max. We don't
    // require either field, but if the user enters both we won't let
    // them save a nonsense range.
    const min = form.client_budget_min === '' ? null : Number(form.client_budget_min);
    const max = form.client_budget_max === '' ? null : Number(form.client_budget_max);
    if (min !== null && max !== null && max < min) {
      toast.error('Client budget max cannot be less than min');
      return;
    }
    setSaving(true);
    try {
      // EDIT path bypasses duplicate detection — you're editing the
      // very record a check would flag. CREATE path runs the
      // cross-channel check before writing so the user gets the 4
      // options from the spec if the customer already exists.
      if (!isEdit) {
        const dup = await checkDuplicate(token, {
          mobile: form.client_mobile,
          email: form.client_email,
          emirates_id: form.emirates_id || '',
        });
        if (dup?.exists) {
          // Stash the payload + show the modal. The modal's "Create
          // New Opportunity" callback will call persistLead with the
          // resolved customer_id so the new row is linked properly.
          setDupResult(dup);
          setPendingPayload(buildPayload());
          setDupOpen(true);
          setSaving(false);
          return;
        }
      }
      await persistLead();
    } catch (e) {
      // Special-case the 409 that the backend's cross-broker guard
      // raises. The ``detail`` is a structured object with the
      // existing broker info so we can render a useful message
      // rather than "[object Object]".
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object') {
        if (detail.error === 'cross_broker_duplicate') {
          toast.error(detail.message
            || `Client is already registered under ${detail.existing_broker_name}`);
        } else {
          toast.error(detail.message || 'Duplicate record blocked');
        }
      } else {
        toast.error(detail || e?.message || 'Could not save the lead');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Lead' : 'Add Lead'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this brokerage-referred lead.'
              : 'Add a lead referred by a brokerage partner.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <Field label="Client name" required>
            <Input
              value={form.client_name}
              onChange={(e) => set('client_name', e.target.value)}
              placeholder="Full name"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Client mobile">
              <Input
                value={form.client_mobile}
                onChange={(e) => set('client_mobile', e.target.value)}
                placeholder="+971…"
              />
            </Field>
            <Field label="Client email">
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => set('client_email', e.target.value)}
                placeholder="client@email.com"
              />
            </Field>
          </div>

          {/* Customer profile extensions — nationality matters in the
              UAE for KYC and visa eligibility; preferred channel tells
              the agent which app to default to (WhatsApp vs Email vs
              Phone). */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nationality">
              <Input
                value={form.customer_nationality}
                onChange={(e) => set('customer_nationality', e.target.value)}
                placeholder="e.g. UAE, Indian, British"
              />
            </Field>
            <Field label="Preferred contact method">
              <Select
                value={form.preferred_contact_method || 'none'}
                onValueChange={(v) => set('preferred_contact_method', v === 'none' ? '' : v)}
              >
                <SelectTrigger><SelectValue placeholder="Preferred channel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not specified —</SelectItem>
                  {CONTACT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* The defining field — bind the lead to a brokerage. */}
          <Field label="Brokerage">
            <Select
              value={form.broker_id || 'none'}
              onValueChange={(v) => set('broker_id', v === 'none' ? '' : v)}
            >
              <SelectTrigger><SelectValue placeholder="Select a brokerage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No brokerage (direct lead) —</SelectItem>
                {brokerages.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Project enquired" required>
              <ProjectMultiSelect
                projects={projects}
                projectsLoaded={projectsLoaded}
                selectedIds={form.project_enquired_ids}
                selectedNames={form.project_enquired_names}
                onChange={(ids, names) => setForm((f) => ({
                  ...f,
                  project_enquired_ids: ids,
                  project_enquired_names: names,
                  // Keep legacy single field aligned with the first
                  // selection so the dashboard column / search keep
                  // working without the new field.
                  project_name: names[0] || '',
                }))}
                fallbackText={form.project_name}
                onFallbackTextChange={(v) => set('project_name', v)}
              />
            </Field>
            <Field label="Unit type">
              <Input
                value={form.unit_type}
                onChange={(e) => set('unit_type', e.target.value)}
                placeholder="e.g. 2BR"
              />
            </Field>
          </div>

          {/* Property-type + preferred unit + preferred location.
              Property type is the asset class (apartment / villa /…)
              while the existing unit_type captures the configuration
              within (2BR, studio, etc.). */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Property type">
              <Select
                value={form.property_type || 'none'}
                onValueChange={(v) => set('property_type', v === 'none' ? '' : v)}
              >
                <SelectTrigger><SelectValue placeholder="Asset class" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not specified —</SelectItem>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Preferred unit #">
              <Input
                value={form.preferred_unit_number}
                onChange={(e) => set('preferred_unit_number', e.target.value)}
                placeholder="e.g. A-1503"
              />
            </Field>
            <Field label="Preferred location">
              <Input
                value={form.preferred_location}
                onChange={(e) => set('preferred_location', e.target.value)}
                placeholder="e.g. Dubai Marina"
              />
            </Field>
          </div>

          {/* Client Budget (AED) — leave both blank if unknown. Fill
              only `min` for a single-figure budget; fill both for a
              range. Display logic on the dashboard handles the format. */}
          <Field label="Client budget (AED)">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <Input
                type="number"
                min="0"
                value={form.client_budget_min}
                onChange={(e) => set('client_budget_min', e.target.value)}
                placeholder="Min or single figure"
              />
              <span className="text-xs text-slate-400">to</span>
              <Input
                type="number"
                min="0"
                value={form.client_budget_max}
                onChange={(e) => set('client_budget_max', e.target.value)}
                placeholder="Max (for a range)"
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Optional. Leave both blank if unknown. Fill only the left field
              for a single figure (e.g. "AED 1.5M"); fill both for a range
              (e.g. "AED 1.5M – 2M").
            </p>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {LEAD_STATUS_LABELS[s] || s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assigned agent">
              <Input
                value={form.assigned_to_name}
                onChange={(e) => set('assigned_to_name', e.target.value)}
                placeholder="Internal agent name"
              />
            </Field>
          </div>

          {/* Assignment context — team / branch and manager give the
              dashboard a way to slice by sales floor and provide an
              escalation contact when the assigned executive is
              unavailable. */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Team / branch">
              <Input
                value={form.team_branch}
                onChange={(e) => set('team_branch', e.target.value)}
                placeholder="e.g. JLT Office · Sales Team A"
              />
            </Field>
            <Field label="Manager">
              <Input
                value={form.manager_name}
                onChange={(e) => set('manager_name', e.target.value)}
                placeholder="Escalation contact"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Payout amount (AED)">
              <Input
                type="number"
                value={form.payout_amount}
                onChange={(e) => set('payout_amount', e.target.value)}
                placeholder="0"
              />
            </Field>
            <Field label="Commission %">
              <Input
                type="number"
                value={form.commission_percent}
                onChange={(e) => set('commission_percent', e.target.value)}
                placeholder="0"
              />
            </Field>
          </div>

          <Field label="Source notes">
            <Textarea
              rows={2}
              value={form.source_notes}
              onChange={(e) => set('source_notes', e.target.value)}
              placeholder="How this lead came in, context worth keeping…"
            />
          </Field>

          {/* The spec's "Notes / Main Overview" field — distinct from
              source_notes which is specifically about how the lead
              originated. This one captures the rest: client
              preferences, broker conversation, internal context. */}
          <Field label="Notes / Main Overview">
            <Textarea
              rows={4}
              value={form.lead_overview}
              onChange={(e) => set('lead_overview', e.target.value)}
              placeholder="Free-form notes about the client, broker conversation, preferences, budget breakdown, timeline…"
            />
          </Field>

          {/* Site visit — pairs with the `viewing` funnel stage. When
              status first crosses into `viewing`, the backend stamps
              the date automatically; users can edit any field here at
              any time. Visible to both Brokerage Hub and Indirect
              Sales since they read the same lead row. */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <CalendarDays size={13} /> Site visit
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Visit date / time">
                <Input
                  type="datetime-local"
                  value={form.site_visit_date}
                  onChange={(e) => set('site_visit_date', e.target.value)}
                />
              </Field>
              <Field label="Visit agent">
                <Input
                  value={form.site_visit_agent}
                  onChange={(e) => set('site_visit_agent', e.target.value)}
                  placeholder="Agent who hosted the walkthrough"
                />
              </Field>
            </div>
            <div className="mt-2">
              <Field label="Outcome">
                <Select
                  value={form.site_visit_outcome || 'none'}
                  onValueChange={(v) => set('site_visit_outcome', v === 'none' ? '' : v)}
                >
                  <SelectTrigger><SelectValue placeholder="Outcome" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not recorded —</SelectItem>
                    <SelectItem value="interested">Interested — proceed to negotiation</SelectItem>
                    <SelectItem value="needs_followup">Needs follow-up</SelectItem>
                    <SelectItem value="not_interested">Not interested</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-2">
              <Field label="Visit feedback">
                <Textarea
                  rows={2}
                  value={form.site_visit_feedback}
                  onChange={(e) => set('site_visit_feedback', e.target.value)}
                  placeholder="What the client said, objections raised, next steps agreed…"
                />
              </Field>
            </div>
          </div>

          {/* Follow-up cadence. last_follow_up_at is auto-stamped
              backend-side on every save, but next_follow_up_at is the
              one the agent has to set explicitly — that's what feeds
              the "overdue follow-up" badge on the dashboard. */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <CalendarDays size={13} /> Follow-up
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Next follow-up">
                <Input
                  type="datetime-local"
                  value={form.next_follow_up_at}
                  onChange={(e) => set('next_follow_up_at', e.target.value)}
                />
              </Field>
              <Field label="Meeting details">
                <Input
                  value={form.meeting_details}
                  onChange={(e) => set('meeting_details', e.target.value)}
                  placeholder="Where / who / what — short summary"
                />
              </Field>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              Last follow-up is stamped automatically every time you save
              this lead.
            </p>
          </div>

          {/* Booking sub-record — captures the unit chosen at
              booking-initiated time, the booking amount paid (token
              money / down payment) and any reservation notes (e.g.
              "Token AED 50k cleared via cheque #4521"). booking_date
              auto-stamps when status first crosses to booking_initiated
              but can be overridden here. */}
          <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              <Building className="h-3 w-3" /> Booking
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Unit selected">
                <Input
                  value={form.booked_unit_number}
                  onChange={(e) => set('booked_unit_number', e.target.value)}
                  placeholder="e.g. A-1503"
                />
              </Field>
              <Field label="Booking amount (AED)">
                <Input
                  type="number"
                  min="0"
                  value={form.booking_amount}
                  onChange={(e) => set('booking_amount', e.target.value)}
                  placeholder="Token / down payment"
                />
              </Field>
              <Field label="Booking date">
                <Input
                  type="datetime-local"
                  value={form.booking_date}
                  onChange={(e) => set('booking_date', e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-2">
              <Field label="Reservation notes">
                <Textarea
                  rows={2}
                  value={form.reservation_notes}
                  onChange={(e) => set('reservation_notes', e.target.value)}
                  placeholder="Cheque references, payment plan summary, special conditions…"
                />
              </Field>
            </div>
          </div>

          {/* Commission tracking — broker-side, NOT the customer
              payment. Split into two questions: do they qualify
              (eligibility), and has the payout happened (payment
              status). Both are kept as enums to make per-broker
              reporting easy. */}
          <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              <DollarSign className="h-3 w-3" /> Commission
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Eligibility">
                <Select
                  value={form.commission_eligibility || 'pending'}
                  onValueChange={(v) => set('commission_eligibility', v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMMISSION_ELIGIBILITY.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment status (to broker)">
                <Select
                  value={form.commission_payment_status || 'pending'}
                  onValueChange={(v) => set('commission_payment_status', v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMMISSION_PAYMENT_STATUS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          {/* UAE document panel — only shown for existing leads since a
              brand-new lead has no id yet to attach files against.
              Telling the user this up-front avoids the "where did the
              upload box go?" confusion when creating a record. */}
          {isEdit ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <FileText size={13} /> Documents (UAE)
              </div>
              <LeadDocumentsPanel leadId={lead.id} />
            </div>
          ) : (
            <p className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-500">
              <FileText size={12} className="mr-1 inline" />
              Save the lead first, then re-open it to upload Emirates ID, passport,
              SPA, NOC, cheque copy and other UAE documents.
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Duplicate detection modal — only mounted when a check actually
        fires, so it doesn't run any work on every modal open. The
        4-action UI is owned by DuplicateCheckModal; we hand it the
        callbacks it needs to either save (Option B) or open the
        existing lead in a new tab (Option A). */}
    <DuplicateCheckModal
      open={dupOpen}
      onOpenChange={setDupOpen}
      duplicate={dupResult}
      prospectivePayload={pendingPayload}
      prospectiveTarget="broker_sales"
      // Lets the modal compare existing leads' broker_id against
      // the one we're creating under — drives the cross-broker
      // conflict UI (first-broker-wins rule).
      prospectiveBrokerId={form.broker_id || null}
      onViewExisting={(leadId, source) => {
        // Best-effort: most "view existing" surfaces are tab pages,
        // not URL-addressable. We close the modal and let the user
        // navigate from the Indirect Sales / Brokerage Hub lists.
        toast.info('Open the existing lead from its dashboard');
        setDupOpen(false);
      }}
      onCreateOpportunity={async (customerId) => {
        setSaving(true);
        try {
          await persistLead(customerId);
        } finally {
          setSaving(false);
        }
      }}
    />
    </>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);

/**
 * Project multi-select. Built on a popover-style dropdown rather than
 * a chained shadcn Select because the shadcn Select doesn't natively
 * do multi-pick. The list is searchable to handle the case where the
 * tenant has many projects.
 *
 * Degraded states:
 *   - projects haven't loaded yet                → show spinner-ish text
 *   - projects loaded but empty                  → show a free-text input
 *     fallback so the form is still usable when /api/projects is
 *     misconfigured or the tenant hasn't seeded its project list yet.
 */
const ProjectMultiSelect = ({
  projects, projectsLoaded, selectedIds, selectedNames,
  onChange, fallbackText, onFallbackTextChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return projects;
    const q = query.toLowerCase();
    return projects.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [projects, query]);

  const toggle = (project) => {
    const idx = selectedIds.indexOf(project.id);
    let nextIds, nextNames;
    if (idx === -1) {
      nextIds = [...selectedIds, project.id];
      nextNames = [...selectedNames, project.name];
    } else {
      nextIds = selectedIds.filter((id) => id !== project.id);
      nextNames = selectedNames.filter((_, i) => i !== idx);
    }
    onChange(nextIds, nextNames);
  };

  const remove = (id, name) => {
    const idx = selectedIds.indexOf(id);
    const nextIds = idx >= 0 ? selectedIds.filter((x) => x !== id) : selectedIds;
    const nextNames = selectedNames.filter((n) => n !== name);
    onChange(nextIds, nextNames);
  };

  // No projects in the master → fall back to a plain text input so the
  // user can still type something. The legacy `project_name` field is
  // what we write to in this mode.
  if (projectsLoaded && projects.length === 0) {
    return (
      <div>
        <Input
          value={fallbackText || ''}
          onChange={(e) => onFallbackTextChange?.(e.target.value)}
          placeholder="e.g. Hatimi Residences"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          No projects in the master yet — type the project name. Add projects via the Project Master to enable multi-select.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[40px] border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 text-left bg-white dark:bg-slate-900 flex items-center justify-between gap-2"
      >
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {selectedNames.length === 0 ? (
            <span className="text-sm text-slate-400">
              {projectsLoaded ? 'Select one or more projects…' : 'Loading projects…'}
            </span>
          ) : (
            selectedNames.map((name, i) => (
              <Badge
                key={selectedIds[i] || name}
                variant="outline"
                className="text-[11px] bg-violet-50 border-violet-200 text-violet-800"
              >
                {name}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(selectedIds[i], name);
                  }}
                  className="ml-1 hover:text-red-600 cursor-pointer"
                  aria-label={`Remove ${name}`}
                >
                  <X size={11} />
                </span>
              </Badge>
            ))
          )}
        </div>
        <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects…"
            className="border-0 border-b border-slate-200 dark:border-slate-700 rounded-none focus-visible:ring-0"
          />
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500">
                No matching projects.
              </div>
            ) : (
              filtered.map((p) => {
                const isSel = selectedIds.includes(p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => toggle(p)}
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 ${
                      isSel ? 'bg-violet-50/50 dark:bg-violet-950/20' : ''
                    }`}
                  >
                    <span className="truncate">{p.name}</span>
                    {isSel && <Check size={14} className="text-violet-600" />}
                  </button>
                );
              })
            )}
          </div>
          <div className="border-t border-slate-200 dark:border-slate-700 px-2 py-1.5 flex justify-between items-center">
            <span className="text-[11px] text-slate-500">
              {selectedIds.length} selected
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
